import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Box, ButtonBase, CircularProgress } from '@mui/material';

import PdfIcon from '~/assets/icons/image-format/pdf.svg?react';
import PngJpgIcon from '~/assets/icons/image-format/pngjpg.svg?react';
import SvgIcon from '~/assets/icons/image-format/svg.svg?react';
import LastStepImage from '~/assets/images/modal/lastStep.png';
import { Button } from '~/components/ui/button';
import { ConfirmModal } from '~/components/ui/ConfirmModal/ConfirmModal';
import { Text } from '~/components/ui/text';
import { QR_CODE_TEMPLATE_PARAM_NAME } from '~/constants/qr-code';
import { useDownloadQr } from '~/queries/qrCodeTable';
import { COLORS } from '~/styles/colors';
import { logger } from '~/utils/logger';

import * as S from './styles';

const imageFormats = [
  { name: 'jpeg', icon: <PngJpgIcon /> },
  { name: 'png', icon: <PngJpgIcon /> },
  { name: 'svg', icon: <SvgIcon /> },
  { name: 'pdf', icon: <PdfIcon /> },
];

type DownloadQrStepProps = {
  onClose: () => void;
  open: boolean;
  id: string;
  imageName: string;
};
export function DownloadQrStep({ id, imageName, onClose, open }: DownloadQrStepProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [checkedFormat, setCheckedFormat] = useState('');
  const { mutateAsync: donwnloadQRAsync, status: statusQR } = useDownloadQr();

  const templateId = searchParams.get(QR_CODE_TEMPLATE_PARAM_NAME) || null;
  const getQrCodeType = () => {
    try {
      if (templateId) {
        return templateId;
      }
      const parts = location.pathname.split('/');
      return parts[3];
    } catch (error) {
      logger.error(error);
    }
  };

  const isLoading = statusQR === 'pending';

  const handleDownload = async () => {
    window.dataLayer?.push({
      event: 'download_qr_code',
      qr_code_id: id,
      qr_code_type: getQrCodeType(),
    });
    await donwnloadQRAsync({ id, format: checkedFormat, name: imageName });
    onClose();
  };

  return (
    <ConfirmModal contentSx={{ maxWidth: '709px' }} onClose={onClose} open={open}>
      <Box sx={S.downloadContainer} className={isLoading ? 'is-loading' : ''}>
        <Box component="img" sx={{ margin: '0 auto' }} src={LastStepImage} alt="Last step" width={228} height={230} />
        <Text variant="h1.5" sx={{ marginTop: '20px' }}>
          {t('downloadStep:title')}
        </Text>
        <Text variant="h1.5">{t('downloadStep:subtitle')}</Text>
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            marginTop: '24px',
            justifyContent: 'center',
          }}
        >
          {imageFormats.map(({ name, icon }) => (
            <ButtonBase
              key={name}
              sx={S.downloadButton}
              className={name === checkedFormat ? 'checked' : undefined}
              onClick={() => {
                setCheckedFormat(name);
              }}
            >
              {isLoading && name === checkedFormat ? (
                <CircularProgress size={52} />
              ) : (
                <>
                  <Box sx={{ background: COLORS.blue50, padding: '10px 14px', borderRadius: '12px' }}>{icon}</Box>
                  <Text variant="t18Medium" sx={S.imageText}>
                    {name}
                  </Text>
                </>
              )}
            </ButtonBase>
          ))}
        </Box>
        <Button disabled={!checkedFormat} onClick={handleDownload} sx={{ marginTop: '40px' }}>
          {t('buttons:download')}
        </Button>
      </Box>
    </ConfirmModal>
  );
}
